import { useQuery } from '@tanstack/react-query';
import { api } from '@/api/client';
import { companyDay, formatDate, formatTime } from '@/format';
import { useAuth } from '@/stores/auth';

/**
 * The live announcements addressed to the signed-in person, and the wording
 * every place that shows them shares: the ticker across the top of the
 * Dashboard, the Announcements board below it, the pop-up both open, and the
 * composer's preview. One query key, so the ticker and the board never send
 * two requests for the same list.
 */

function fetchFeed() {
  return api.get('/api/v1/announcements/feed');
}

export type FeedItem = Awaited<ReturnType<typeof fetchFeed>>[number];
export type Priority = FeedItem['priority'];

export const FEED_KEY = ['announcements', 'feed'] as const;
const FEED_REFRESH_MS = 60_000;

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
/** How long a notice carries the "New" tag. */
const NEW_FOR_MS = 24 * HOUR;

export function useAnnouncementFeed() {
  const signedIn = useAuth((s) => !!s.user);
  return useQuery({
    queryKey: FEED_KEY,
    queryFn: fetchFeed,
    refetchInterval: FEED_REFRESH_MS,
    enabled: signedIn,
  });
}

export interface NoticeText {
  headline: string;
  /** Null when there is no body, or it is only spaces. */
  body: string | null;
  /** Null when the author is no longer on file. */
  author: string | null;
}

/**
 * The words to show in the person's language. Each part falls back to
 * English on its own, so an Urdu headline with no Urdu body still shows the
 * English body rather than nothing.
 */
export function localize(item: FeedItem, language: string): NoticeText {
  const urdu = language === 'ur';
  const pick = (ur: string | null | undefined, en: string | null | undefined) =>
    blankToNull(urdu ? ur : null) ?? blankToNull(en);

  return {
    headline: pick(item.headlineUr, item.headlineEn) ?? '',
    body: pick(item.bodyUr, item.bodyEn),
    author: item.postedBy ? pick(item.postedBy.nameUr, item.postedBy.name) : null,
  };
}

function blankToNull(text: string | null | undefined): string | null {
  return text && text.trim() ? text : null;
}

/**
 * When a notice went live: its start time, or when it was published if it
 * went live straight away. Null for a notice from before publish times were
 * kept, which then shows no time rather than a wrong one.
 */
export function liveSince(item: FeedItem): Date | null {
  const when = item.startsAt ?? item.publishedAt;
  if (!when) return null;
  const date = new Date(when);
  return Number.isNaN(date.getTime()) ? null : date;
}

const relative = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });

/**
 * "just now", "5 minutes ago", "3 hours ago", "yesterday", "4 days ago", then
 * the date. Days are counted in the company's time zone. A time a little in
 * the future — the server's clock ahead of this computer's — reads "just now"
 * rather than "in 2 minutes".
 */
export function postedAgo(when: Date | null, now: number): string | null {
  if (!when) return null;
  const ago = now - when.getTime();
  if (ago < MINUTE) return 'just now';
  if (ago < HOUR) return relative.format(-Math.floor(ago / MINUTE), 'minute');

  const days = companyDay(new Date(now)) - companyDay(when);
  if (days <= 0) return relative.format(-Math.floor(ago / HOUR), 'hour');
  if (days < 7) return relative.format(-days, 'day');
  return formatDate(when);
}

/** Whether the notice went live in the last day. */
export function isNew(item: FeedItem, now: number): boolean {
  const since = liveSince(item);
  return since !== null && now - since.getTime() < NEW_FOR_MS;
}

/** "Ends today, 5:00 pm", "Ends tomorrow", "Until 30/10/2026", or null when it has no end. */
export function liveUntil(item: FeedItem, now: number): string | null {
  if (!item.endsAt) return null;
  const end = new Date(item.endsAt);
  if (Number.isNaN(end.getTime())) return null;

  const days = companyDay(end) - companyDay(new Date(now));
  if (days <= 0) return `Ends today, ${formatTime(end)}`;
  if (days === 1) return 'Ends tomorrow';
  return `Until ${formatDate(end)}`;
}

export const PRIORITY_LABEL: Record<Priority, string | null> = {
  URGENT: 'Urgent',
  IMPORTANT: 'Important',
  INFO: null,
};
